import { Circle, Group, Layer, Line, Path, Rect, Stage, Text } from "react-konva";
import { useEffect, useMemo, useRef, useState } from "react";
import type { KonvaEventObject } from "konva/lib/Node";
import type { Unit, Vec2 } from "../../lib/types";
import { useElementSize } from "../../lib/useViewportSize";
import { useFitToRoom } from "../../lib/useFitToRoom";
import { useStagePanZoom } from "../../lib/useStagePanZoom";
import { getEdgePanDelta } from "../../lib/edgePan";
import { Locate } from "lucide-react";
import {
  distance,
  formatLength,
  midpoint,
  moveVertex,
  slideEdge,
  snap,
  snapToAxis,
  wallDirection,
} from "../../lib/geom";
import { SketchOverlay, type SketchOutcome } from "./SketchOverlay";
import type { ProcessStrokeOptions } from "../../lib/sketch";
import { gridStepsFor } from "../../lib/gridStep";
import { annotationScale } from "../../lib/canvasLabels";
import { GridLegend } from "./GridLegend";

/** Hand-traced lucide `Lock` glyph (24×24 viewBox), reused from the room editor. */
const LOCK_ICON_PATH =
  "M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2zm2 0V7a5 5 0 0 1 10 0v4";

const MAJOR_GRID_INCHES = 12;
const SNAP_INCHES = 1;
const AXIS_SNAP_INCHES = 6;

const GRID_MAJOR_STROKE = "#bcc0c8";
const GRID_COARSE_STROKE = "#dadde2";
const GRID_FINE_STROKE = "#eef0f3";

const WALL_THICKNESS_PX = 8;
const WALL_HIT_PX = 36;
const VERTEX_RADIUS_PX = 11;
const VERTEX_HIT_PX = 28;
const ACCENT = "#2a70f5";

/** Furniture pieces are small; start the blank canvas at a 6'×6' area. */
const FURNITURE_BLANK_EXTENT_IN = { width: 72, height: 72 };

type DragKind =
  | {
      kind: "wall";
      index: number;
      originPxX: number;
      originPxY: number;
      normX: number;
      normY: number;
      startPoints: Vec2[];
    }
  | { kind: "vertex"; index: number; startPoints: Vec2[]; originPxX: number; originPxY: number };

interface Props {
  /** Draft polygon (inches). Empty until the first stroke is committed. */
  points: Vec2[];
  /** Live edit commit during wall/vertex drags. */
  onChangePoints: (points: Vec2[]) => void;
  /** Called once after a drag commits geometry — lets the host re-sync wall
   *  locks to the new polygon. */
  onCommitDrag?: (points: Vec2[]) => void;
  /** Drag start / end / pinch-cancel, so the host can record one undo step. */
  onGestureStart?: () => void;
  onGestureEnd?: () => void;
  onGestureCancel?: () => void;
  /** Walls the user has pinned to a specific length, in inches. */
  lockedWallLengths: Record<number, number>;
  unit: Unit;
  /** "sketch" → draw mode (SketchOverlay captures input); "edit" → drag
   *  walls/vertices; "shapes" → stamp primitives (click to place). */
  tool: "edit" | "sketch" | "shapes";
  /** Primitive outline (inches, centered on the origin) shown as a ghost
   *  under the pointer while `tool === "shapes"`. */
  shapeGhost?: Vec2[] | null;
  /** Whether the ghost stamp will cut (true) or add (false). Styles the ghost. */
  shapeGhostCuts?: boolean;
  /** Fired with the click position (inches) while `tool === "shapes"`. */
  onPlaceShape?: (at: Vec2) => void;
  selectedWall: number | null;
  selectedVertex: number | null;
  onSelectWall: (i: number | null) => void;
  onSelectVertex: (i: number | null) => void;
  onSketchResult: (outcome: SketchOutcome) => void;
  /** Dashed ghost of a cleaned sketch awaiting confirm. */
  previewPoints?: Vec2[] | null;
  /** Small-piece `processStroke` overrides forwarded to the SketchOverlay. */
  sketchProcessOptions?: Partial<ProcessStrokeOptions>;
}

/**
 * A furniture-scale sketch + edit canvas. A store-agnostic adaptation of
 * {@link import("./RoomEditorCanvas").RoomEditorCanvas} — same grid, drag-to-draw
 * sketching, wall/vertex direct manipulation, wall-length labels, lock pills,
 * and pan/zoom — but driven entirely by props (local draft state in the
 * furniture creator modal) instead of the design store, and without the
 * collaborator/presence layers.
 */
export function FurnitureEditorCanvas({
  points,
  onChangePoints,
  onCommitDrag,
  onGestureStart,
  onGestureEnd,
  onGestureCancel,
  lockedWallLengths,
  unit,
  tool,
  selectedWall,
  selectedVertex,
  onSelectWall,
  onSelectVertex,
  onSketchResult,
  previewPoints = null,
  sketchProcessOptions,
  shapeGhost = null,
  shapeGhostCuts = false,
  onPlaceShape,
}: Props) {
  const [containerRef, size] = useElementSize<HTMLDivElement>();
  const [ghostAt, setGhostAt] = useState<Vec2 | null>(null);

  const [fitBasisPoints, setFitBasisPoints] = useState(() =>
    points.map((p) => ({ ...p }))
  );
  const dragRef = useRef<DragKind | null>(null);
  const dragMovedRef = useRef(false);
  const lastCanvasPointerRef = useRef<Vec2 | null>(null);
  const lastScenePointerRef = useRef<Vec2 | null>(null);
  const edgePanFrameRef = useRef<number | null>(null);
  const suppressFitSyncForPointsRef = useRef<Vec2[] | null>(null);
  const shiftHeldRef = useRef(false);
  const spaceHeldRef = useRef(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    return () => {
      if (edgePanFrameRef.current != null) {
        window.cancelAnimationFrame(edgePanFrameRef.current);
      }
    };
  }, []);

  // Shift axis-lock: re-apply the in-progress drag when Shift toggles so the
  // preview snaps without needing the pointer to wiggle.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Shift") return;
      if (shiftHeldRef.current) return;
      shiftHeldRef.current = true;
      const ds = dragRef.current;
      const p = lastScenePointerRef.current;
      if (ds && p) applyDragAtPointer(ds, p);
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.key !== "Shift") return;
      if (!shiftHeldRef.current) return;
      shiftHeldRef.current = false;
      const ds = dragRef.current;
      const p = lastScenePointerRef.current;
      if (ds && p) applyDragAtPointer(ds, p);
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Space-to-pan: hold Space and drag to pan, primarily for sketch mode where
  // the SketchOverlay otherwise eats every pointer event.
  useEffect(() => {
    function isSpaceEventForUs(e: KeyboardEvent): boolean {
      if (e.code !== "Space" && e.key !== " ") return false;
      if (e.metaKey || e.ctrlKey || e.altKey) return false;
      const t = e.target;
      if (t instanceof HTMLElement) {
        const tag = t.tagName;
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          tag === "BUTTON"
        )
          return false;
        if (t.isContentEditable) return false;
        if (t.getAttribute("role") === "button") return false;
      }
      return true;
    }
    function onKeyDown(e: KeyboardEvent) {
      if (!isSpaceEventForUs(e)) return;
      e.preventDefault();
      if (spaceHeldRef.current) return;
      spaceHeldRef.current = true;
      setSpaceHeld(true);
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.code !== "Space" && e.key !== " ") return;
      if (!spaceHeldRef.current) return;
      spaceHeldRef.current = false;
      setSpaceHeld(false);
    }
    function releaseSpace() {
      if (!spaceHeldRef.current) return;
      spaceHeldRef.current = false;
      setSpaceHeld(false);
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", releaseSpace);
    document.addEventListener("visibilitychange", releaseSpace);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", releaseSpace);
      document.removeEventListener("visibilitychange", releaseSpace);
    };
  }, []);

  // Keep the editor's base scale stable during direct manipulation.
  useEffect(() => {
    if (suppressFitSyncForPointsRef.current === points) return;
    if (!dragRef.current) setFitBasisPoints(points.map((p) => ({ ...p })));
  }, [points]);

  const fit = useFitToRoom(
    fitBasisPoints,
    size.width,
    size.height,
    56,
    FURNITURE_BLANK_EXTENT_IN
  );
  const panZoom = useStagePanZoom({ onInterrupt: () => cancelLocalDrag() });

  function getPointer(e: KonvaEventObject<unknown>): Vec2 | null {
    const stage = e.target.getStage();
    if (!stage) return null;
    lastCanvasPointerRef.current = stage.getPointerPosition();
    const native = e.evt as { shiftKey?: boolean } | undefined;
    if (native && typeof native.shiftKey === "boolean") {
      shiftHeldRef.current = native.shiftKey;
    }
    const scene = stage.getRelativePointerPosition();
    if (scene) lastScenePointerRef.current = scene;
    return scene;
  }

  function stopEdgePan() {
    if (edgePanFrameRef.current != null) {
      window.cancelAnimationFrame(edgePanFrameRef.current);
      edgePanFrameRef.current = null;
    }
    lastCanvasPointerRef.current = null;
  }

  function applyDragAtPointer(ds: DragKind, p: Vec2) {
    if (ds.kind === "wall") {
      const dxPx = p.x - ds.originPxX;
      const dyPx = p.y - ds.originPxY;
      const dxIn = dxPx / fit.scale;
      const dyIn = dyPx / fit.scale;
      const delta = dxIn * ds.normX + dyIn * ds.normY;
      const snapped = snap(delta, SNAP_INCHES);
      if (Math.abs(snapped) < 0.001) return;
      dragMovedRef.current = true;
      const newPoints = slideEdge(ds.startPoints, ds.index, snapped);
      suppressFitSyncForPointsRef.current = newPoints;
      onChangePoints(newPoints);
    } else if (ds.kind === "vertex") {
      const orig = ds.startPoints[ds.index];
      let targetRoom = fit.pxToRoom(p);

      if (shiftHeldRef.current) {
        const dxPx = Math.abs(p.x - ds.originPxX);
        const dyPx = Math.abs(p.y - ds.originPxY);
        if (dxPx >= dyPx) {
          targetRoom = { x: targetRoom.x, y: orig.y };
        } else {
          targetRoom = { x: orig.x, y: targetRoom.y };
        }
      }

      const others = ds.startPoints.filter((_, idx) => idx !== ds.index);
      const ax = snapToAxis(targetRoom, others, AXIS_SNAP_INCHES);
      let snapped: Vec2 = {
        x: snap(ax.pos.x, SNAP_INCHES),
        y: snap(ax.pos.y, SNAP_INCHES),
      };

      if (shiftHeldRef.current) {
        const dxPx = Math.abs(p.x - ds.originPxX);
        const dyPx = Math.abs(p.y - ds.originPxY);
        if (dxPx >= dyPx) {
          snapped = { x: snapped.x, y: orig.y };
        } else {
          snapped = { x: orig.x, y: snapped.y };
        }
      }

      if (
        Math.abs(snapped.x - orig.x) < 0.5 &&
        Math.abs(snapped.y - orig.y) < 0.5
      )
        return;
      dragMovedRef.current = true;
      const newPoints = moveVertex(ds.startPoints, ds.index, snapped);
      suppressFitSyncForPointsRef.current = newPoints;
      onChangePoints(newPoints);
    }
  }

  function startEdgePan() {
    if (edgePanFrameRef.current != null) return;
    const tick = () => {
      edgePanFrameRef.current = null;
      const ds = dragRef.current;
      const pointer = lastCanvasPointerRef.current;
      if (!ds || !pointer) return;

      const delta = getEdgePanDelta(pointer, size);
      if (Math.abs(delta.x) > 0.01 || Math.abs(delta.y) > 0.01) {
        panZoom.panBy(delta.x, delta.y);
        const p = panZoom.stageRef.current?.getRelativePointerPosition();
        if (p) applyDragAtPointer(ds, p);
      }

      edgePanFrameRef.current = window.requestAnimationFrame(tick);
    };
    edgePanFrameRef.current = window.requestAnimationFrame(tick);
  }

  /** A second finger landed mid-drag: put the outline back, no undo step. */
  function cancelLocalDrag() {
    const ds = dragRef.current;
    if (ds) {
      const moved = dragMovedRef.current;
      dragRef.current = null;
      dragMovedRef.current = false;
      stopEdgePan();
      if (onGestureCancel) onGestureCancel();
      else if (moved) {
        suppressFitSyncForPointsRef.current = null;
        onChangePoints(ds.startPoints);
      }
      forceUpdate((x) => x + 1);
    }
  }

  /** Extra fingers belong to the pinch handler, so they bubble to the Stage. */
  function canStartDrag(e: KonvaEventObject<unknown>): boolean {
    if (dragRef.current) return false;
    const touches = (e.evt as TouchEvent | undefined)?.touches;
    return !touches || touches.length < 2;
  }

  function startWallDrag(e: KonvaEventObject<unknown>, i: number) {
    if (!canStartDrag(e)) return;
    e.cancelBubble = true;
    const p = getPointer(e);
    if (!p) return;
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const dir = wallDirection(a, b);
    dragRef.current = {
      kind: "wall",
      index: i,
      originPxX: p.x,
      originPxY: p.y,
      normX: -dir.y,
      normY: dir.x,
      startPoints: points.map((q) => ({ ...q })),
    };
    dragMovedRef.current = false;
    forceUpdate((x) => x + 1);
    onGestureStart?.();
  }

  function startVertexDrag(e: KonvaEventObject<unknown>, i: number) {
    if (!canStartDrag(e)) return;
    e.cancelBubble = true;
    const p = getPointer(e);
    dragRef.current = {
      kind: "vertex",
      index: i,
      startPoints: points.map((q) => ({ ...q })),
      originPxX: p?.x ?? fit.pointToPx(points[i]).x,
      originPxY: p?.y ?? fit.pointToPx(points[i]).y,
    };
    dragMovedRef.current = false;
    forceUpdate((x) => x + 1);
    onGestureStart?.();
  }

  function onStagePointerMove(e: KonvaEventObject<unknown>) {
    const evt = e.evt as { touches?: TouchList } | undefined;
    if (evt && evt.touches && evt.touches.length > 1) {
      cancelLocalDrag();
      return;
    }
    const ds = dragRef.current;
    const p = getPointer(e);
    if (tool === "shapes" && p) {
      // Snap the stamp ghost to the grid so shapes line up with the outline.
      setGhostAt({ x: snap(fit.pxToRoom(p).x, SNAP_INCHES), y: snap(fit.pxToRoom(p).y, SNAP_INCHES) });
    }
    if (!ds) return;
    if (!p) return;
    startEdgePan();
    applyDragAtPointer(ds, p);
  }

  function onStagePointerUp(e: KonvaEventObject<unknown>) {
    const ds = dragRef.current;
    if (!ds) return;
    const movedSomething = dragMovedRef.current;
    if (ds.kind === "wall") onSelectWall(ds.index);
    else onSelectVertex(ds.index);
    dragRef.current = null;
    dragMovedRef.current = false;
    stopEdgePan();
    if (movedSomething) onCommitDrag?.(suppressFitSyncForPointsRef.current ?? points);
    onGestureEnd?.();
    forceUpdate((x) => x + 1);
    e.cancelBubble = true;
  }

  function onStageBackgroundTap(e: KonvaEventObject<unknown>) {
    if (dragRef.current) return;
    if (tool === "shapes" && onPlaceShape) {
      const p = getPointer(e);
      if (p) {
        const at = fit.pxToRoom(p);
        onPlaceShape({ x: snap(at.x, SNAP_INCHES), y: snap(at.y, SNAP_INCHES) });
      }
      return;
    }
    if (e.target === e.target.getStage()) {
      onSelectWall(null);
      onSelectVertex(null);
    }
  }

  const ghostFlat = useMemo(() => {
    if (tool !== "shapes" || !shapeGhost || !ghostAt || shapeGhost.length < 3) return null;
    const arr: number[] = [];
    for (const p of shapeGhost) {
      const pp = fit.pointToPx({ x: p.x + ghostAt.x, y: p.y + ghostAt.y });
      arr.push(pp.x, pp.y);
    }
    return arr;
  }, [tool, shapeGhost, ghostAt, fit]);

  // ---- Computed visuals ----

  const flatPolygon = useMemo(() => {
    const arr: number[] = [];
    for (const p of points) {
      const pp = fit.pointToPx(p);
      arr.push(pp.x, pp.y);
    }
    return arr;
  }, [points, fit]);

  const gridStep = useMemo(() => gridStepsFor(panZoom.scale), [panZoom.scale]);
  /** Scale for the edge-length pills — see `canvasLabels.ts`. */
  const labelK = annotationScale(panZoom.scale);

  const gridLines = useMemo(() => {
    const empty = {
      major: [] as number[][],
      coarse: [] as number[][],
      fine: [] as number[][],
    };
    if (
      fit.scale === 0 ||
      !Number.isFinite(fit.scale) ||
      panZoom.scale === 0 ||
      !Number.isFinite(panZoom.scale) ||
      !Number.isFinite(panZoom.x) ||
      !Number.isFinite(panZoom.y) ||
      size.width === 0 ||
      size.height === 0
    ) {
      return empty;
    }
    const stageLeft = -panZoom.x / panZoom.scale;
    const stageTop = -panZoom.y / panZoom.scale;
    const stageRight = (size.width - panZoom.x) / panZoom.scale;
    const stageBottom = (size.height - panZoom.y) / panZoom.scale;
    const tl = fit.pxToRoom({ x: stageLeft, y: stageTop });
    const br = fit.pxToRoom({ x: stageRight, y: stageBottom });
    const viewMinX = Math.min(tl.x, br.x);
    const viewMinY = Math.min(tl.y, br.y);
    const viewMaxX = Math.max(tl.x, br.x);
    const viewMaxY = Math.max(tl.y, br.y);
    const padX = viewMaxX - viewMinX;
    const padY = viewMaxY - viewMinY;
    const minX = viewMinX - padX;
    const minY = viewMinY - padY;
    const maxX = viewMaxX + padX;
    const maxY = viewMaxY + padY;
    const major: number[][] = [];
    const coarse: number[][] = [];
    const fine: number[][] = [];
    const fineStep = gridStep.fine;
    const coarseStep = gridStep.coarse;
    if (fineStep <= 0 || !Number.isFinite(fineStep)) return empty;
    const startX = Math.floor(minX / fineStep) * fineStep;
    const startY = Math.floor(minY / fineStep) * fineStep;
    if (
      !Number.isFinite(startX) ||
      !Number.isFinite(startY) ||
      !Number.isFinite(maxX) ||
      !Number.isFinite(maxY)
    ) {
      return empty;
    }
    const MAX_LINES = 10000;
    const endX = Math.min(maxX, startX + MAX_LINES * fineStep);
    const endY = Math.min(maxY, startY + MAX_LINES * fineStep);
    for (let x = startX; x <= endX; x += fineStep) {
      const p1 = fit.pointToPx({ x, y: minY });
      const p2 = fit.pointToPx({ x, y: maxY });
      const isMajor = Math.abs(x % MAJOR_GRID_INCHES) < 1e-6;
      const isCoarse = !isMajor && Math.abs(x % coarseStep) < 1e-6;
      const seg: number[] = [p1.x, p1.y, p2.x, p2.y];
      if (isMajor) major.push(seg);
      else if (isCoarse) coarse.push(seg);
      else fine.push(seg);
    }
    for (let y = startY; y <= endY; y += fineStep) {
      const p1 = fit.pointToPx({ x: minX, y });
      const p2 = fit.pointToPx({ x: maxX, y });
      const isMajor = Math.abs(y % MAJOR_GRID_INCHES) < 1e-6;
      const isCoarse = !isMajor && Math.abs(y % coarseStep) < 1e-6;
      const seg: number[] = [p1.x, p1.y, p2.x, p2.y];
      if (isMajor) major.push(seg);
      else if (isCoarse) coarse.push(seg);
      else fine.push(seg);
    }
    return { major, coarse, fine };
  }, [
    fit,
    panZoom.x,
    panZoom.y,
    panZoom.scale,
    size.height,
    size.width,
    gridStep.coarse,
    gridStep.fine,
  ]);

  const wallSegments = useMemo(() => {
    return points.map((p, i) => {
      const next = points[(i + 1) % points.length];
      return {
        index: i,
        a: p,
        b: next,
        mid: midpoint(p, next),
        len: distance(p, next),
      };
    });
  }, [points]);

  const hasShape = points.length >= 3;
  const dragging = dragRef.current;

  // Baked curves (rounded corners, bent edges, circle stamps) produce runs
  // of short, gently-turning segments. Showing a handle + length pill on
  // every sample is noise, so we hide them for "curve sample" vertices and
  // their tiny edges. The endpoints of a curve stay editable.
  const CURVE_EDGE_IN = 4;
  const CURVE_TURN_DEG = 35;
  const isCurveSample = useMemo(() => {
    const n = points.length;
    return points.map((p, i) => {
      if (n < 5) return false;
      const prev = points[(i - 1 + n) % n];
      const next = points[(i + 1) % n];
      const d1 = distance(prev, p);
      const d2 = distance(p, next);
      if (d1 >= CURVE_EDGE_IN || d2 >= CURVE_EDGE_IN) return false;
      const a1 = Math.atan2(p.y - prev.y, p.x - prev.x);
      const a2 = Math.atan2(next.y - p.y, next.x - p.x);
      let turn = Math.abs(((a2 - a1) * 180) / Math.PI);
      if (turn > 180) turn = 360 - turn;
      return turn < CURVE_TURN_DEG;
    });
  }, [points]);
  const isCurveEdge = (i: number) => {
    const n = points.length;
    return isCurveSample[i] && isCurveSample[(i + 1) % n];
  };
  const sketchActive = tool === "sketch";
  const panMode = sketchActive && spaceHeld;

  useEffect(() => {
    const stage = panZoom.stageRef.current;
    if (!stage) return;
    const container = stage.container();
    if (sketchActive) {
      container.style.cursor = spaceHeld ? "grab" : "default";
    }
  }, [sketchActive, spaceHeld, panZoom.stageRef]);

  const previewFlat = useMemo(() => {
    if (!previewPoints || previewPoints.length < 3) return null;
    const arr: number[] = [];
    for (const p of previewPoints) {
      const pp = fit.pointToPx(p);
      arr.push(pp.x, pp.y);
    }
    return arr;
  }, [previewPoints, fit]);

  return (
    <div ref={containerRef} className="absolute inset-0 select-none touch-none">
      {size.width > 0 && size.height > 0 && (
        <Stage
          ref={panZoom.stageRef}
          width={size.width}
          height={size.height}
          scaleX={panZoom.scale}
          scaleY={panZoom.scale}
          x={panZoom.x}
          y={panZoom.y}
          draggable={sketchActive ? spaceHeld : !dragging}
          onDragStart={(e) => {
            if (!spaceHeldRef.current) return;
            const stage = e.target.getStage();
            if (stage) stage.container().style.cursor = "grabbing";
          }}
          onDragEnd={(e) => {
            panZoom.onDragEnd(e);
            const stage = e.target.getStage();
            if (stage && sketchActive) {
              stage.container().style.cursor = spaceHeldRef.current
                ? "grab"
                : "default";
            }
          }}
          onWheel={(e) => panZoom.onWheel(e)}
          onClick={onStageBackgroundTap}
          onTap={onStageBackgroundTap}
          onMouseMove={onStagePointerMove}
          onMouseUp={onStagePointerUp}
          onMouseLeave={onStagePointerUp}
          onTouchStart={panZoom.onTouchStart}
          onTouchMove={(e) => {
            if (e.evt.touches && e.evt.touches.length > 1) {
              cancelLocalDrag();
              panZoom.onTouchMove(e);
              return;
            }
            onStagePointerMove(e);
          }}
          onTouchEnd={(e) => {
            panZoom.onTouchEnd(e);
            onStagePointerUp(e);
          }}
        >
          {/* Grid: three-tier adaptive layer. */}
          <Layer listening={false}>
            {gridLines.fine.map((pts, idx) => (
              <Line
                key={`f-${idx}`}
                points={pts}
                stroke={GRID_FINE_STROKE}
                strokeWidth={1}
                opacity={gridStep.fineOpacity}
              />
            ))}
            {gridLines.coarse.map((pts, idx) => (
              <Line
                key={`c-${idx}`}
                points={pts}
                stroke={GRID_COARSE_STROKE}
                strokeWidth={1}
              />
            ))}
            {gridLines.major.map((pts, idx) => (
              <Line
                key={`M-${idx}`}
                points={pts}
                stroke={GRID_MAJOR_STROKE}
                strokeWidth={1}
              />
            ))}
          </Layer>

          {/* Furniture polygon */}
          <Layer listening={!sketchActive && hasShape}>
            {hasShape && (
              <Line
                points={flatPolygon}
                closed
                fill="#eef2f8"
                stroke="#0c0c10"
                strokeWidth={WALL_THICKNESS_PX}
                lineJoin="round"
                shadowColor="#000"
                shadowBlur={20}
                shadowOpacity={0.06}
                shadowOffsetY={4}
                listening={false}
              />
            )}

            {hasShape &&
              wallSegments.map((w) => {
                const a = fit.pointToPx(w.a);
                const b = fit.pointToPx(w.b);
                const isSelected = selectedWall === w.index;
                const isDragging =
                  dragging?.kind === "wall" && dragging.index === w.index;
                return (
                  <Line
                    key={`wall-${w.index}`}
                    points={[a.x, a.y, b.x, b.y]}
                    stroke={
                      isSelected || isDragging ? ACCENT : "rgba(0,0,0,0.001)"
                    }
                    strokeWidth={
                      isSelected || isDragging
                        ? WALL_THICKNESS_PX + 4
                        : WALL_THICKNESS_PX
                    }
                    hitStrokeWidth={WALL_HIT_PX}
                    lineCap="round"
                    opacity={isSelected || isDragging ? 0.55 : 1}
                    onMouseDown={(e) => startWallDrag(e, w.index)}
                    onTouchStart={(e) => startWallDrag(e, w.index)}
                    onMouseEnter={(e) => {
                      const stage = e.target.getStage();
                      if (stage) stage.container().style.cursor = "grab";
                    }}
                    onMouseLeave={(e) => {
                      const stage = e.target.getStage();
                      if (stage && !dragRef.current)
                        stage.container().style.cursor = "default";
                    }}
                  />
                );
              })}

            {/* Wall length labels (tappable) */}
            {hasShape &&
              wallSegments.map((w) => {
                if (isCurveEdge(w.index) && selectedWall !== w.index) return null;
                const m = fit.pointToPx(w.mid);
                const a = fit.pointToPx(w.a);
                const b = fit.pointToPx(w.b);
                const angle =
                  (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
                const upright = angle > 90 || angle < -90 ? angle + 180 : angle;
                const isSelected = selectedWall === w.index;
                const isDragging =
                  dragging?.kind === "wall" && dragging.index === w.index;
                const isLocked = w.index in lockedWallLengths;
                const label = formatLength(w.len, unit);
                const padX = 10;
                const charW = 7;
                const lockGutter = isLocked ? 16 : 0;
                const textWidth = Math.max(
                  48,
                  label.length * charW + padX * 2 + lockGutter
                );
                const active = isSelected || isDragging;
                const bg = active ? ACCENT : isLocked ? "#1f1813" : "#0c0c10";
                return (
                  <Group
                    key={`lbl-${w.index}`}
                    x={m.x}
                    y={m.y}
                    rotation={upright}
                    scaleX={labelK}
                    scaleY={labelK}
                    onMouseDown={(e) => startWallDrag(e, w.index)}
                    onTouchStart={(e) => startWallDrag(e, w.index)}
                    onMouseEnter={(e) => {
                      const stage = e.target.getStage();
                      if (stage) stage.container().style.cursor = "pointer";
                    }}
                    onMouseLeave={(e) => {
                      const stage = e.target.getStage();
                      if (stage && !dragRef.current)
                        stage.container().style.cursor = "default";
                    }}
                  >
                    <Rect
                      x={-textWidth / 2}
                      y={-13}
                      width={textWidth}
                      height={26}
                      fill={bg}
                      cornerRadius={13}
                      stroke={isLocked && !active ? "#f59e0b" : undefined}
                      strokeWidth={isLocked && !active ? 1.5 : 0}
                      shadowColor="#000"
                      shadowBlur={6}
                      shadowOpacity={0.18}
                      shadowOffsetY={2}
                    />
                    {isLocked && (
                      <Group
                        x={-textWidth / 2 + 8}
                        y={-5}
                        scaleX={10 / 24}
                        scaleY={10 / 24}
                        listening={false}
                      >
                        <Path
                          data={LOCK_ICON_PATH}
                          stroke={active ? "#ffffff" : "#fbbf24"}
                          strokeWidth={2.5}
                          fill={undefined}
                          lineCap="round"
                          lineJoin="round"
                        />
                      </Group>
                    )}
                    <Text
                      text={label}
                      x={-textWidth / 2 + lockGutter}
                      y={-7}
                      width={textWidth - lockGutter}
                      align="center"
                      fontSize={13}
                      fontStyle="600"
                      fontFamily="Inter, sans-serif"
                      fill="#ffffff"
                      listening={false}
                    />
                  </Group>
                );
              })}

            {/* Vertex handles */}
            {hasShape &&
              points.map((p, i) => {
                if (isCurveSample[i] && selectedVertex !== i) return null;
                const pp = fit.pointToPx(p);
                const isSelected = selectedVertex === i;
                const isDragging =
                  dragging?.kind === "vertex" && dragging.index === i;
                const active = isSelected || isDragging;
                return (
                  <Group
                    key={`v-${i}`}
                    x={pp.x}
                    y={pp.y}
                    onMouseDown={(e) => startVertexDrag(e, i)}
                    onTouchStart={(e) => startVertexDrag(e, i)}
                    onMouseEnter={(e) => {
                      const stage = e.target.getStage();
                      if (stage) stage.container().style.cursor = "grab";
                    }}
                    onMouseLeave={(e) => {
                      const stage = e.target.getStage();
                      if (stage && !dragRef.current)
                        stage.container().style.cursor = "default";
                    }}
                  >
                    {active && (
                      <Circle
                        radius={VERTEX_RADIUS_PX + 10}
                        fill={`${ACCENT}22`}
                        listening={false}
                      />
                    )}
                    <Circle radius={VERTEX_HIT_PX} opacity={0} />
                    <Circle
                      radius={VERTEX_RADIUS_PX}
                      fill="#ffffff"
                      stroke={active ? ACCENT : "#0c0c10"}
                      strokeWidth={active ? 3 : 2.5}
                      shadowColor="#000"
                      shadowBlur={active ? 10 : 6}
                      shadowOpacity={active ? 0.25 : 0.15}
                      shadowOffsetY={2}
                      listening={false}
                    />
                    <Circle
                      radius={4}
                      fill={active ? ACCENT : "#0c0c10"}
                      listening={false}
                    />
                  </Group>
                );
              })}
          </Layer>

          {/* Ghost preview of the cleaned sketch polygon awaiting confirm. */}
          {previewFlat && (
            <Layer listening={false}>
              <Line
                points={previewFlat}
                closed
                stroke="#2a70f5"
                strokeWidth={4}
                dash={[10, 6]}
                opacity={0.85}
                lineJoin="round"
                lineCap="round"
              />
            </Layer>
          )}

          {/* Stamp ghost for the Shapes tool. */}
          {ghostFlat && (
            <Layer listening={false}>
              <Line
                points={ghostFlat}
                closed
                fill={shapeGhostCuts ? "rgba(244,63,94,0.16)" : "rgba(42,112,245,0.16)"}
                stroke={shapeGhostCuts ? "#f43f5e" : "#2a70f5"}
                strokeWidth={2}
                dash={[6, 4]}
                lineJoin="round"
              />
            </Layer>
          )}

          {/* Sketch tool capture layer (sits on top of everything). */}
          {sketchActive && (
            <SketchOverlay
              key={panMode ? "pan" : "draw"}
              width={size.width}
              height={size.height}
              view={{ x: panZoom.x, y: panZoom.y, scale: panZoom.scale }}
              fit={fit}
              roomPoints={points}
              appendMode={false}
              panMode={panMode}
              processOptions={sketchProcessOptions}
              onResult={onSketchResult}
            />
          )}

        </Stage>
      )}

      {/* Drag hint: a screen-fixed HUD so it doesn't drift or scale with pan/zoom. */}
      {dragging && (
        <div className="pointer-events-none absolute inset-x-0 top-3.5 z-10 flex justify-center">
          <div className="rounded-full bg-accent-500/95 px-3.5 py-1.5 text-caption font-semibold text-white shadow-sm">
            {dragging.kind === "wall"
              ? "Slide edge to resize"
              : shiftHeldRef.current
                ? "Axis locked — release Shift to free up"
                : "Drag corner — hold Shift to lock to an axis"}
          </div>
        </div>
      )}

      {panZoom.isAdjusted && (
        <button
          onClick={() => {
            setFitBasisPoints(points.map((p) => ({ ...p })));
            panZoom.reset();
          }}
          aria-label="Recenter"
          className="absolute right-3 bottom-3 z-10 inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white ring-1 ring-ink-200 text-xs font-medium text-ink-700 shadow-sm hover:bg-ink-50 active:bg-ink-100 transition-colors"
        >
          <Locate className="size-3.5" />
          Recenter
        </button>
      )}

      <GridLegend step={gridStep} unit={unit} />
    </div>
  );
}
